/**
 * AFL pilot format rules — the only format this sport ships with.
 *
 * Owner-set rules (docs/afl-cards-and-costs.md):
 *   - 60-card decks — enforced when you PLAY; drafts save at any size.
 *   - At most ONE legendary card in the whole deck (deck-total, not per-card).
 *   - Players rare-or-better: 1 copy each (singletons). Uncommon and below are
 *     uncapped.
 *   - NO mandatory card-type minimums — build any mix you like (even all
 *     players); stadiums/weather/actions are optional.
 *
 * Per docs/sports-tcg-rules.md, format rules are sport-local.
 */

import type { AnyCard, Rarity } from './cards.js';
import {
  actionCards,
  playerCards,
  stadiumCards,
  weatherCards,
} from './cards.js';

export type FormatKey = 'afl-pilot';

export type FormatRules = {
  /** Exact deck size required for legality. */
  deckSize: number;
  /** Maximum legendary cards across the whole deck (total, not per-card). */
  maxLegendaryTotal: number;
  /** Rarities at/above which a PLAYER is restricted to a single copy. */
  singletonPlayerRarities: ReadonlySet<Rarity>;
  /** Minimum number of cards of a given kind required. */
  familyMin: Partial<Record<AnyCard['kind'], number>>;
};

export type Format = {
  key: FormatKey;
  name: string;
  gameKey: string;
  rules: FormatRules;
};

/** Rare-or-better — the tier at which a PLAYER is held to a single copy (a
 *  light power cap). Uncommon and below are uncapped, so a deck can run
 *  multiples of its workhorse players. */
const RARE_OR_BETTER: ReadonlySet<Rarity> = new Set<Rarity>([
  'rare',
  'epic',
  'legendary',
]);

export const AFL_PILOT_FORMAT: Format = {
  key: 'afl-pilot',
  name: 'Australian Rules Football Pilot',
  gameKey: 'afl-microformat',
  rules: {
    deckSize: 60,
    maxLegendaryTotal: 1,
    singletonPlayerRarities: RARE_OR_BETTER,
    // No mandatory card-type minimums — a deck may be any mix (or all players).
    // The only legality bars are a full 60 cards, at most one legendary, and
    // rare-or-better players as singletons.
    familyMin: {},
  },
};

// ---------------------------------------------------------------------------
// Pure functions — no IO, no React.
// ---------------------------------------------------------------------------

export type DeckEntry = { cardId: string; qty: number };

export type DeckSummary = {
  totalCards: number;
  byKind: Record<string, number>;
  byRarity: Record<string, number>;
};

export function summariseDeck(entries: readonly DeckEntry[], pool: readonly AnyCard[]): DeckSummary {
  const byId = new Map(pool.map((c) => [c.id, c] as const));
  const summary: DeckSummary = { totalCards: 0, byKind: {}, byRarity: {} };
  for (const entry of entries) {
    const card = byId.get(entry.cardId);
    if (!card) continue; // Orphan entries (deleted cards) are filtered earlier.
    summary.totalCards += entry.qty;
    summary.byKind[card.kind] = (summary.byKind[card.kind] ?? 0) + entry.qty;
    summary.byRarity[card.rarity] = (summary.byRarity[card.rarity] ?? 0) + entry.qty;
  }
  return summary;
}

export type ValidationResult =
  | { ok: true }
  | { ok: false; errors: string[] };

/**
 * Per-card copy limit for legality:
 *   - players uncommon-or-better → 1 (singleton);
 *   - everything else → uncapped (the deck-total legendary cap is checked
 *     separately, so a non-player legendary still falls under maxLegendaryTotal).
 */
export function copyLimit(card: AnyCard, format: Format = AFL_PILOT_FORMAT): number {
  if (card.kind === 'player' && format.rules.singletonPlayerRarities.has(card.rarity)) {
    return 1;
  }
  return Infinity;
}

export function validateDeck(
  entries: readonly DeckEntry[],
  pool: readonly AnyCard[],
  format: Format = AFL_PILOT_FORMAT,
): ValidationResult {
  const errors: string[] = [];
  const byId = new Map(pool.map((c) => [c.id, c] as const));
  const summary = summariseDeck(entries, pool);

  if (summary.totalCards !== format.rules.deckSize) {
    errors.push(
      `Deck must have exactly ${format.rules.deckSize} cards (currently ${summary.totalCards}).`,
    );
  }

  const legendaries = summary.byRarity['legendary'] ?? 0;
  if (legendaries > format.rules.maxLegendaryTotal) {
    errors.push(
      `At most ${format.rules.maxLegendaryTotal} legendary card${format.rules.maxLegendaryTotal === 1 ? '' : 's'} per deck (have ${legendaries}).`,
    );
  }

  for (const entry of entries) {
    const card = byId.get(entry.cardId);
    if (!card) {
      errors.push(`Unknown card: ${entry.cardId}.`);
      continue;
    }
    const limit = copyLimit(card, format);
    if (entry.qty > limit) {
      errors.push(
        `Too many copies of ${card.name} (max ${limit === Infinity ? '∞' : limit}).`,
      );
    }
  }

  for (const [kind, min] of Object.entries(format.rules.familyMin)) {
    if (typeof min !== 'number') continue;
    const have = summary.byKind[kind] ?? 0;
    if (have < min) {
      errors.push(`Need at least ${min} ${kind} card${min === 1 ? '' : 's'} (have ${have}).`);
    }
  }

  return errors.length === 0 ? { ok: true } : { ok: false, errors };
}

// ---------------------------------------------------------------------------
// Bot deck generator
// ---------------------------------------------------------------------------

/**
 * Builds a randomised but format-conforming 60-card deck for an AI bot.
 *
 * Composition (always legal under AFL_PILOT_FORMAT):
 *   - up to 14 distinct uncommon+ non-legendary players, 1 copy each;
 *   - common/trash players (uncapped) padded to fill the deck to 60 player
 *     + non-player mix;
 *   - 2 distinct actions × 5, 2 distinct weather × 4, 1 stadium × 4;
 *   - no legendaries (0 ≤ 1 is legal, and keeps 4-copy stacks simple).
 *
 * Deterministic per seed.
 */
export function buildRandomBotDeck(seed: number): DeckEntry[] {
  const rng = makeLcg(seed);
  const entries: DeckEntry[] = [];

  // Non-player families first (fixed counts).
  // Exclude spec (set/suppress/relief) actions so bot + starter decks stay
  // byte-identical to before the call→action merge (those were never in them).
  const actions = pickDistinct(actionCards, 2, rng, (c) => c.rarity !== 'legendary' && !c.spec);
  for (const a of actions) entries.push({ cardId: a.id, qty: 5 }); // 10
  const weathers = pickDistinct(weatherCards, 2, rng, (c) => c.rarity !== 'legendary');
  for (const w of weathers) entries.push({ cardId: w.id, qty: 4 }); // 8
  const stadiums = pickDistinct(stadiumCards, 1, rng, (c) => c.rarity !== 'legendary');
  for (const s of stadiums) entries.push({ cardId: s.id, qty: 4 }); // 4

  const nonPlayerTotal = entries.reduce((n, e) => n + e.qty, 0); // 22
  const playerBudget = 60 - nonPlayerTotal; // 38

  // Singleton uncommon+ non-legendary players.
  const singletons = pickDistinct(
    playerCards,
    14,
    rng,
    (c) => c.rarity !== 'legendary' && c.rarity !== 'common' && c.rarity !== 'trash',
  );
  const distinct = Math.min(singletons.length, playerBudget);
  for (let i = 0; i < distinct; i++) entries.push({ cardId: singletons[i]!.id, qty: 1 });

  // Fill the rest with copies of common/trash players (uncapped), spread
  // across a few so it isn't a 24-stack of one card.
  let remaining = playerBudget - distinct;
  const bulk = pickDistinct(playerCards, 4, rng, (c) => c.rarity === 'common' || c.rarity === 'trash');
  if (bulk.length === 0) {
    // No commons available — fall back to more singletons (shouldn't happen).
    const more = singletons.slice(distinct, distinct + remaining);
    for (const p of more) entries.push({ cardId: p.id, qty: 1 });
  } else {
    let i = 0;
    while (remaining > 0) {
      const card = bulk[i % bulk.length]!;
      const existing = entries.find((e) => e.cardId === card.id);
      if (existing) existing.qty += 1;
      else entries.push({ cardId: card.id, qty: 1 });
      remaining -= 1;
      i += 1;
    }
  }

  return entries;
}

/**
 * A fixed, legal 60-card STARTER deck — the default a new player can play
 * straight away, and the deck the server seats a vs-AI creator with when they
 * haven't chosen one. Deterministic (a stable seed through the bot-deck
 * generator), so it's always the same representative, format-legal mix.
 */
export const AFL_STARTER_SEED = 0x57a27;

export function buildStarterDeck(): DeckEntry[] {
  return buildRandomBotDeck(AFL_STARTER_SEED);
}

/** Linear congruential generator — small, deterministic, good enough. */
function makeLcg(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function pickDistinct<T>(
  pool: readonly T[],
  count: number,
  rng: () => number,
  predicate?: (item: T) => boolean,
): T[] {
  const filtered = predicate ? pool.filter(predicate) : [...pool];
  if (filtered.length <= count) return [...filtered];
  const indices = filtered.map((_, i) => i);
  for (let i = 0; i < count; i++) {
    const j = i + Math.floor(rng() * (indices.length - i));
    const tmp = indices[i]!;
    indices[i] = indices[j]!;
    indices[j] = tmp;
  }
  return indices.slice(0, count).map((i) => filtered[i]!);
}
